/**
 * How far to slide a hint bubble sideways from centred-on-its-control so that
 * it stays on screen (BRAI-685).
 *
 * Centred is the shape people read a tooltip as — the arrow sits in the middle
 * and the box hangs evenly either side — so that is the starting point, and
 * the only reason to leave it is an edge the bubble would otherwise cross.
 * Measured at phone width: centred over the Start button, 82px into the
 * control row, a 224px bubble began at -30px and lost its first characters.
 *
 * Lives apart from the component because exporting a plain function beside one
 * breaks Fast Refresh, and because the arithmetic is worth testing on its own:
 * jsdom has no layout, so nothing about this is exercised by rendering.
 *
 * @param anchorCentre Middle of the control, in viewport pixels.
 * @param bubbleWidth Rendered width of the bubble.
 * @param viewportWidth Width to stay inside.
 * @param margin Smallest gap to leave at either edge.
 * @returns Pixels to add to the centred position: `0` whenever centred fits,
 *   positive to push right off the left edge, negative to pull left off the
 *   right one.
 */
export function hintHorizontalShift({
  anchorCentre,
  bubbleWidth,
  viewportWidth,
  margin,
}: {
  anchorCentre: number
  bubbleWidth: number
  viewportWidth: number
  margin: number
}): number {
  const ideal = anchorCentre - bubbleWidth / 2
  const furthestRight = viewportWidth - bubbleWidth - margin

  // A bubble wider than the screen allows cannot honour both edges. The left
  // one wins, because that is where the sentence starts: clipped at the end
  // still reads, clipped at the start does not.
  if (furthestRight < margin) return margin - ideal

  return Math.min(Math.max(ideal, margin), furthestRight) - ideal
}
